import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const ThemeContext = createContext({ isDark: false, themeMode: 'auto', setThemeMode: () => {} });

const STORAGE_KEY = 'theme_mode'; // 'light' | 'dark' | 'auto'

/** Evening = 18:00–05:59 local time */
function isEvening() {
  const h = new Date().getHours();
  return h >= 18 || h < 6;
}

function resolveIsDark(mode) {
  if (mode === 'light') return false;
  if (mode === 'dark') return true;
  return isEvening(); // auto
}

export const ThemeProvider = ({ children }) => {
  const [themeMode, setThemeModeState] = useState(() => {
    return localStorage.getItem(STORAGE_KEY) || 'auto';
  });

  const applyTheme = useCallback((mode) => {
    const dark = resolveIsDark(mode);
    document.documentElement.classList.toggle('dark', dark);
    return dark;
  }, []);

  const [isDark, setIsDark] = useState(() => applyTheme(themeMode));

  const setThemeMode = useCallback((mode) => {
    localStorage.setItem(STORAGE_KEY, mode);
    setThemeModeState(mode);
    setIsDark(applyTheme(mode));
  }, [applyTheme]);

  // Minute-tick interval — only meaningful in 'auto' mode
  useEffect(() => {
    applyTheme(themeMode);

    if (themeMode !== 'auto') return;

    const apply = () => {
      const dark = isEvening();
      setIsDark(dark);
      document.documentElement.classList.toggle('dark', dark);
    };

    const msToNextMinute = (60 - new Date().getSeconds()) * 1000;
    let interval;
    const timeout = setTimeout(() => {
      apply();
      interval = setInterval(apply, 60_000);
    }, msToNextMinute);

    return () => {
      clearTimeout(timeout);
      if (interval) clearInterval(interval);
    };
  }, [themeMode, applyTheme]);

  return (
    <ThemeContext.Provider value={{ isDark, themeMode, setThemeMode }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
